/**
 * Friend Service - Handles friend-related operations in the Superior Payroll application
 * 
 * This service provides functions for sending, accepting, and rejecting friend requests,
 * as well as retrieving lists of friends, friend requests, and company directory.
 */

import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  query, 
  where, 
  Timestamp, 
  serverTimestamp, 
  setDoc, 
  addDoc, 
  deleteDoc,
  writeBatch,
  limit
} from 'firebase/firestore';
import { UserProfile } from '../contexts/AuthContext';
import { sendEmailNotification, EmailTemplate } from './emailService';

// Interface for the result of friend operations
interface FriendOperationResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

/**
 * Atomically apply field updates to two user documents in a single batch.
 * The friend-graph mutations (send/accept/reject) all touch exactly two users'
 * arrayUnion/arrayRemove fields; this centralizes the writeBatch boilerplate they
 * previously repeated.
 */
async function updateTwoUsers(
  aId: string,
  aUpdate: Record<string, any>,
  bId: string,
  bUpdate: Record<string, any>
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, 'users', aId), aUpdate);
  batch.update(doc(db, 'users', bId), bUpdate);
  await batch.commit();
}

/**
 * Get basic profile information of a user by ID
 */
export const getUserBasicInfo = async (userId: string): Promise<FriendOperationResult> => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      return { 
        success: false, 
        error: 'User not found' 
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    
    // Return only basic profile info needed for friend UI
    return {
      success: true,
      data: {
        uid: userId,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        photoURL: userData.photoURL || '',
        department: userData.department || '',
        title: userData.position || '',
        email: userData.email || ''
      }
    };
  } catch (error: any) {
    console.error('Error getting user info:', error);
    return {
      success: false,
      error: error.message || 'Failed to get user information'
    };
  }
};

/**
 * Get company directory (all users) with basic profile information
 */
export const getCompanyDirectory = async (): Promise<FriendOperationResult> => {
  try {
    // Query users collection with a limit for performance
    const usersQuery = query(
      collection(db, 'users'),
      limit(100) // Limit to 100 users for performance
    );

    const querySnapshot = await getDocs(usersQuery);

    if (querySnapshot.empty) {
      return {
        success: true,
        data: []
      };
    }

    const users = querySnapshot.docs.map(doc => {
      const userData = doc.data() as UserProfile;
      return {
        uid: doc.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        photoURL: userData.photoURL || '',
        department: userData.department || '',
        title: userData.position || '',
        email: userData.email || '',
        role: userData.role || ''
      };
    }).filter(user => user.role !== 'Administrator' && user.role !== 'Owner'); // Filter out admin and owner accounts

    return {
      success: true,
      data: users
    };
  } catch (error: any) {
    console.error('[FRIEND SERVICE] Error getting company directory:', error);
    return {
      success: false,
      error: error.message || 'Failed to get company directory'
    };
  }
};

/**
 * Send a friend request to another user
 */
export const sendFriendRequest = async (fromUserId: string, toUserId: string) => {
  try {
    // Verify both users exist
    const [fromUserDoc, toUserDoc] = await Promise.all([
      getDoc(doc(db, 'users', fromUserId)),
      getDoc(doc(db, 'users', toUserId))
    ]);

    if (!fromUserDoc.exists() || !toUserDoc.exists()) {
      console.error('[FRIEND SERVICE] One or both users do not exist');
      return { success: false, error: 'One or both users do not exist' };
    }

    const fromUser = fromUserDoc.data();
    const toUser = toUserDoc.data();

    // Check if a request already exists
    const requestRef = collection(db, 'friendRequests');
    const q = query(
      requestRef, 
      where('fromUserId', '==', fromUserId),
      where('toUserId', '==', toUserId)
    );
    
    const requestSnapshot = await getDocs(q);
    
    if (!requestSnapshot.empty) {
      console.error('[FRIEND SERVICE] Friend request already sent');
      return { success: false, error: 'Friend request already sent' };
    }
    
    // Create friend request in friendRequests collection
    await addDoc(requestRef, {
      fromUserId,
      toUserId,
      status: 'pending',
      createdAt: serverTimestamp()
    });

    // Also update user documents for backward compatibility: sender gains a
    // sentRequest, recipient gains a friendRequest.
    await updateTwoUsers(
      fromUserId,
      { sentRequests: arrayUnion(toUserId) },
      toUserId,
      { friendRequests: arrayUnion(fromUserId) }
    );

    // Send email notification
    const emailParams = {
      to: toUser.email,
      subject: `${fromUser.firstName} ${fromUser.lastName} sent you a friend request`,
      templateName: EmailTemplate.FRIEND_REQUEST,
      templateData: {
        requesterId: fromUserId,
        requesterName: `${fromUser.firstName} ${fromUser.lastName}`,
        requesterPhotoURL: fromUser.photoURL || '',
        requesterDepartment: fromUser.department || '',
        recipientName: `${toUser.firstName}`,
        appUrl: window.location.origin,
        friendsPageUrl: `${window.location.origin}/friends`
      }
    };
    
    const emailResult = await sendEmailNotification(emailParams);

    return { success: true, message: 'Friend request sent successfully' };
  } catch (error: any) {
    console.error('[FRIEND SERVICE] Error sending friend request:', error);
    return { success: false, error: error.message || 'Failed to send friend request' };
  }
};

/**
 * Accept a friend request from another user
 */
export const acceptFriendRequest = async (currentUserId: string, requesterId: string) => {
  try {
    // Verify both users exist
    const [currentUserDoc, requesterDoc] = await Promise.all([
      getDoc(doc(db, 'users', currentUserId)),
      getDoc(doc(db, 'users', requesterId))
    ]);

    if (!currentUserDoc.exists() || !requesterDoc.exists()) {
      return { success: false, error: 'One or both users do not exist' };
    }

    const currentUser = currentUserDoc.data();
    const requester = requesterDoc.data();

    // Check if a pending request exists
    const requestRef = collection(db, 'friendRequests');
    const q = query(
      requestRef, 
      where('fromUserId', '==', requesterId),
      where('toUserId', '==', currentUserId),
      where('status', '==', 'pending')
    );
    
    const requestSnapshot = await getDocs(q);
    
    if (requestSnapshot.empty) {
      return { success: false, error: 'No pending friend request found' };
    }
    
    // Update request status to accepted
    const requestDocRef = doc(db, 'friendRequests', requestSnapshot.docs[0].id);
    await updateDoc(requestDocRef, {
      status: 'accepted',
      updatedAt: serverTimestamp()
    });
    
    // Add each user to the other's friends list and clean up request arrays
    // Both users gain each other as a friend; clear the pending request on each.
    await updateTwoUsers(
      currentUserId,
      { friends: arrayUnion(requesterId), friendRequests: arrayRemove(requesterId) },
      requesterId,
      { friends: arrayUnion(currentUserId), sentRequests: arrayRemove(currentUserId) }
    );

    // Send email notification to the requester
    await sendEmailNotification({
      to: requester.email,
      subject: `${currentUser.firstName} ${currentUser.lastName} accepted your friend request`,
      templateName: EmailTemplate.FRIEND_REQUEST_ACCEPTED,
      templateData: {
        acceptorId: currentUserId,
        acceptorName: `${currentUser.firstName} ${currentUser.lastName}`,
        acceptorPhotoURL: currentUser.photoURL || '',
        acceptorDepartment: currentUser.department || '',
        requesterName: `${requester.firstName}`,
        appUrl: window.location.origin,
        friendsPageUrl: `${window.location.origin}/friends`
      }
    });
    
    return { success: true, message: 'Friend request accepted successfully' };
  } catch (error: any) {
    console.error('Error accepting friend request:', error);
    return { success: false, error: error.message || 'Failed to accept friend request' };
  }
};

/**
 * Reject a friend request from another user
 */
export const rejectFriendRequest = async (currentUserId: string, requesterId: string) => {
  try {
    // Verify both users exist
    const [currentUserDoc, requesterDoc] = await Promise.all([
      getDoc(doc(db, 'users', currentUserId)),
      getDoc(doc(db, 'users', requesterId))
    ]);

    if (!currentUserDoc.exists() || !requesterDoc.exists()) {
      return { success: false, error: 'One or both users do not exist' };
    }

    const currentUser = currentUserDoc.data();
    const requester = requesterDoc.data();

    // Check if a pending request exists
    const requestRef = collection(db, 'friendRequests');
    const q = query(
      requestRef, 
      where('fromUserId', '==', requesterId),
      where('toUserId', '==', currentUserId),
      where('status', '==', 'pending')
    );
    
    const requestSnapshot = await getDocs(q);
    
    if (requestSnapshot.empty) {
      return { success: false, error: 'No pending friend request found' };
    }
    
    // Update request status to rejected
    const requestDocRef = doc(db, 'friendRequests', requestSnapshot.docs[0].id);
    await updateDoc(requestDocRef, {
      status: 'rejected',
      updatedAt: serverTimestamp()
    });
    
    // Clean up user document arrays
    // Clear the pending request from both sides.
    await updateTwoUsers(
      currentUserId,
      { friendRequests: arrayRemove(requesterId) },
      requesterId,
      { sentRequests: arrayRemove(currentUserId) }
    );
    
    // Send email notification to the requester
    await sendEmailNotification({
      to: requester.email,
      subject: `Response to your friend request`,
      templateName: EmailTemplate.FRIEND_REQUEST_DECLINED,
      templateData: {
        declinerId: currentUserId,
        declinerName: `${currentUser.firstName} ${currentUser.lastName}`,
        requesterName: `${requester.firstName}`,
        appUrl: window.location.origin,
        friendsPageUrl: `${window.location.origin}/friends`
      }
    });
    
    return { success: true, message: 'Friend request rejected successfully' };
  } catch (error: any) {
    console.error('Error rejecting friend request:', error);
    return { success: false, error: error.message || 'Failed to reject friend request' };
  }
};

/**
 * Cancel a sent friend request
 */
export const cancelFriendRequest = async (userId: string, receiverId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const receiverRef = doc(db, 'users', receiverId);
    
    const userDoc = await getDoc(userRef);
    const receiverDoc = await getDoc(receiverRef);
    
    if (!userDoc.exists() || !receiverDoc.exists()) {
      return {
        success: false,
        error: 'One or both users do not exist'
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    
    // Verify that a sent request exists
    if (!userData.sentRequests?.includes(receiverId)) {
      return {
        success: false,
        error: 'No sent friend request found for this user'
      };
    }
    
    // Find and delete the request document in the friendRequests collection
    const requestRef = collection(db, 'friendRequests');
    const q = query(
      requestRef, 
      where('fromUserId', '==', userId),
      where('toUserId', '==', receiverId)
    );
    
    const requestSnapshot = await getDocs(q);
    
    // Use a batch to perform all operations atomically
    const batch = writeBatch(db);
    
    // Remove from user documents
    batch.update(userRef, {
      sentRequests: arrayRemove(receiverId)
    });
    
    batch.update(receiverRef, {
      friendRequests: arrayRemove(userId)
    });
    
    // Delete the request document if found
    if (!requestSnapshot.empty) {
      const requestDoc = requestSnapshot.docs[0];
      batch.delete(doc(db, 'friendRequests', requestDoc.id));
    }
    
    await batch.commit();
    
    return {
      success: true,
      message: 'Friend request canceled'
    };
  } catch (error: any) {
    console.error('Error canceling friend request:', error);
    return {
      success: false,
      error: error.message || 'Failed to cancel friend request'
    };
  }
};

/**
 * Remove a friend
 */
export const removeFriend = async (userId: string, friendId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const friendRef = doc(db, 'users', friendId);
    
    const userDoc = await getDoc(userRef);
    const friendDoc = await getDoc(friendRef);
    
    if (!userDoc.exists() || !friendDoc.exists()) {
      return {
        success: false,
        error: 'One or both users do not exist'
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    
    // Verify that they are friends
    if (!userData.friends?.includes(friendId)) {
      return {
        success: false,
        error: 'You are not friends with this user'
      };
    }
    
    // Remove from both users' friend lists
    await updateDoc(userRef, {
      friends: arrayRemove(friendId)
    });
    
    await updateDoc(friendRef, {
      friends: arrayRemove(userId)
    });
    
    return {
      success: true,
      message: 'Friend removed'
    };
  } catch (error: any) {
    console.error('Error removing friend:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove friend'
    };
  }
};

/**
 * Get list of friends with basic profile information
 */
export const getFriendsList = async (userId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'User not found'
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    const friendIds = userData.friends || [];
    
    if (friendIds.length === 0) {
      return {
        success: true,
        data: []
      };
    }
    
    // Get profile information for each friend
    const friendsPromises = friendIds.map(friendId => getUserBasicInfo(friendId));
    const friendsResults = await Promise.all(friendsPromises);
    
    // Filter out any failed lookups and map to just the data
    const friends = friendsResults
      .filter(result => result.success)
      .map(result => result.data);
    
    return {
      success: true,
      data: friends
    };
  } catch (error: any) {
    console.error('Error getting friends list:', error);
    return {
      success: false,
      error: error.message || 'Failed to get friends list'
    };
  }
};

/**
 * Get list of incoming friend requests with basic profile information
 */
export const getFriendRequests = async (userId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'User not found'
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    const requestIds = userData.friendRequests || [];
    
    if (requestIds.length === 0) {
      return {
        success: true,
        data: []
      };
    }
    
    // Get profile information for each requester
    const requestersPromises = requestIds.map(requesterId => getUserBasicInfo(requesterId));
    const requestersResults = await Promise.all(requestersPromises);
    
    // Filter out any failed lookups and map to just the data
    const requesters = requestersResults
      .filter(result => result.success)
      .map(result => result.data);
    
    return {
      success: true,
      data: requesters
    };
  } catch (error: any) {
    console.error('Error getting friend requests:', error);
    return {
      success: false,
      error: error.message || 'Failed to get friend requests'
    };
  }
};

/**
 * Get list of sent friend requests with basic profile information
 */
export const getSentFriendRequests = async (userId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'User not found'
      };
    }
    
    const userData = userDoc.data() as UserProfile;
    const sentIds = userData.sentRequests || [];
    
    if (sentIds.length === 0) {
      return {
        success: true,
        data: []
      };
    }
    
    // Get profile information for each recipient
    const recipientsPromises = sentIds.map(recipientId => getUserBasicInfo(recipientId));
    const recipientsResults = await Promise.all(recipientsPromises);
    
    // Filter out any failed lookups and map to just the data
    const recipients = recipientsResults
      .filter(result => result.success)
      .map(result => result.data);
    
    return {
      success: true,
      data: recipients
    };
  } catch (error: any) {
    console.error('Error getting sent friend requests:', error);
    return {
      success: false,
      error: error.message || 'Failed to get sent friend requests'
    };
  }
};

/**
 * Find a user by email address
 */
export const findUserByEmail = async (email: string): Promise<FriendOperationResult> => {
  try {
    if (!email || email.trim() === '') {
      return {
        success: false,
        error: 'Email is required'
      };
    }

    // Query Firestore for users with the specified email
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email.trim().toLowerCase())
    );

    const querySnapshot = await getDocs(usersQuery);

    if (querySnapshot.empty) {
      return {
        success: false,
        error: 'No user found with this email address'
      };
    }

    // We found a user with this email
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data() as UserProfile;

    return {
      success: true,
      data: {
        uid: userDoc.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        photoURL: userData.photoURL || '',
        department: userData.department || '',
        title: userData.position || '',
        email: userData.email || ''
      }
    };
  } catch (error: any) {
    console.error('[FRIEND SERVICE] Error finding user by email:', error);
    return {
      success: false,
      error: error.message || 'Failed to find user'
    };
  }
};

/**
 * Initialize friend request arrays for a new user
 */
export const initializeFriendRequests = async (userId: string): Promise<FriendOperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      friends: [],
      friendRequests: [],
      sentRequests: []
    });
    
    return {
      success: true,
      message: 'Friend request arrays initialized'
    };
  } catch (error: any) {
    console.error('Error initializing friend request arrays:', error);
    return {
      success: false,
      error: error.message || 'Failed to initialize friend request arrays'
    };
  }
}; 